/** Terms, privacy policy and health-data notice (drafts until 2.0) and the user guide (/help),
 * readable without signing in. */
import { Fragment, type ReactNode } from "react";
import { safeUrl } from "@/api/client";
import guide from "@/legal/guide.md?raw";
import health from "@/legal/health.md?raw";
import privacy from "@/legal/privacy.md?raw";
import terms from "@/legal/terms.md?raw";

export const LEGAL: Record<string, string> = { terms, privacy, health, guide };

/** Inline **bold** and [links](https://…) (links only through safeUrl). */
function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1]) out.push(<b key={m.index}>{m[1]}</b>);
    else {
      const local = m[3]!.startsWith("/") && !m[3]!.startsWith("//"); // a page of this app
      const href = safeUrl(m[3]) ?? (local ? m[3] : undefined);
      out.push(
        href ? (
          <a key={m.index} href={href}>
            {m[2]}
          </a>
        ) : (
          m[2]
        ),
      );
    }
    last = re.lastIndex;
  }
  out.push(text.slice(last));
  return out;
}

const BULLET = /^- /;
const NUMBER = /^\d+\. /;

/** A block that is a list: items start with "- " (or "1. "); an item may wrap onto indented lines,
 * which continue it. Returns null for anything else. */
export function listItems(lines: string[]): { ordered: boolean; items: string[] } | null {
  const first = lines[0] ?? "";
  const marker = BULLET.test(first) ? BULLET : NUMBER.test(first) ? NUMBER : null;
  if (!marker) return null;
  const items: string[] = [];
  for (const l of lines) {
    if (marker.test(l)) items.push(l.replace(marker, "").trim());
    else if (/^\s+\S/.test(l) && items.length) items[items.length - 1] += ` ${l.trim()}`;
    else return null;
  }
  return { ordered: marker === NUMBER, items };
}

/** A small Markdown subset: headings, paragraphs, bullet and numbered lists (items may wrap onto
 * indented lines) and pipe tables. */
export function Markdown({ text }: { text: string }) {
  const blocks = text.trim().split(/\n\s*\n/);
  return (
    <>
      {blocks.map((b, i) => {
        const lines = b.split("\n");
        if (b.startsWith("# ")) return <h1 key={i}>{b.slice(2)}</h1>;
        if (b.startsWith("## ")) return <h2 key={i}>{b.slice(3)}</h2>;
        const items = listItems(lines);
        if (items)
          return items.ordered ? (
            <ol key={i}>
              {items.items.map((l, j) => (
                <li key={j}>{inline(l)}</li>
              ))}
            </ol>
          ) : (
            <ul key={i}>
              {items.items.map((l, j) => (
                <li key={j}>{inline(l)}</li>
              ))}
            </ul>
          );
        if (lines.every((l) => l.startsWith("|"))) {
          const rows = lines.filter((l) => !/^\|[\s|-]+\|$/.test(l)).map((l) => l.split("|").slice(1, -1));
          return (
            <div className="table-wrap" tabIndex={0} role="region" aria-label="Table" key={i}>
              <table>
                <thead>
                  <tr>
                    {rows[0]!.map((c, j) => (
                      <th key={j}>{c.trim()}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(1).map((r, j) => (
                    <tr key={j}>
                      {r.map((c, k) => (
                        <td key={k}>{inline(c.trim())}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        return (
          <p key={i}>
            {lines.map((l, j) => (
              <Fragment key={j}>
                {j > 0 && " "}
                {inline(l)}
              </Fragment>
            ))}
          </p>
        );
      })}
    </>
  );
}

export function LegalPage({ doc }: { doc: string }) {
  const text = Object.hasOwn(LEGAL, doc) ? LEGAL[doc] : undefined;
  return (
    <main className="legal">
      {text ? <Markdown text={text} /> : <p>Not found.</p>}
      <p className="faint">
        <a href="/help">How it works</a> · <a href="/legal/terms">Terms</a> ·{" "}
        <a href="/legal/privacy">Privacy</a> · <a href="/legal/health">Health data</a> · <a href="/">Bason</a>
      </p>
    </main>
  );
}
